<script setup lang="ts">
type Props = {
  showIcon?: boolean;
};

const { showIcon = false } = defineProps<Props>();

const { t } = useTexts();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const { setAuthInitialTab } = useLayoutStore();

const {
  isNewAuthButtonsEnabled,
  isWithinMigrationPeriod,
  userMigrationNewDomain,
} = useFeatureFlagsNew();

const migrationNewDomain = computed(() => {
  return userMigrationNewDomain.value.startsWith('https://')
    ? userMigrationNewDomain.value
    : `https://${userMigrationNewDomain.value}`;
});

const textLogin = computed(() => {
  return t('header.login');
});

const textRegister = computed(() => {
  return t('header.register');
});

const buttonsAddClass = computed(() => {
  return isNewAuthButtonsEnabled.value ? 'new' : '';
});

function openRegisterTab() {
  setAuthInitialTab('register');
  openLogin();
}
</script>

<template>
  <div
    v-if="!isAuthenticated"
    class="buttons"
    :class="buttonsAddClass"
  >
    <button
      v-if="!isWithinMigrationPeriod"
      class="flex h-[30px] items-center gap-2.5 [background:radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%)] px-2.5 py-[7px] rounded-[9px] text-white text-center text-xs font-semibold leading-[10px]"
      type="button"
      @click="openRegisterTab"
    >
      <span>
        {{ textRegister }}
      </span>
    </button>

    <NuxtLink
      v-else
      class="btn btn-cta btn-register gap-1"
      :class="[buttonsAddClass, appNameLower()]"
      external
      :to="migrationNewDomain"
    >
      <NuxtIcon name="external" />
      {{ $t('header.access_new_site') }}
    </NuxtLink>

    <button
      class="flex h-[30px] items-center gap-2.5 [background:#FFF] px-2.5 py-[7px] rounded-[9px] text-[#000717] text-center text-xs font-semibold leading-[10px]"
      @click="openLogin"
    >
      <NuxtIcon
        v-if="showIcon"
        name="sign_in"
      />
      {{ textLogin }}
    </button>
  </div>

  <UserHeaderButtons v-else />
</template>

<style scoped lang="scss">
.buttons {
  @apply flex gap-0 pt-px xl:pt-0 relative z-10;

  &.new {
    @apply gap-1.5 md:gap-2;
  }

  .btn {
    @apply h-[30.88px] text-sm lg:text-base;

    &.new {
      @apply rounded-3xl h-[26px] md:h-[30px];
    }
  }

  .btn-register {
    @apply px-[.63rem] bg-header-register-bg text-base xl:px-3 flex gap-2 items-center justify-center relative font-semibold text-header-register-text/80 md:font-medium md:text-header-register-text/50;

    &:hover {
      @apply opacity-40;
    }

    &.new {
      @apply bg-header-login-bg text-header-login-text py-1.5 md:py-2 px-3 md:px-4 text-[12px] md:text-[14px] leading-3 font-semibold whitespace-nowrap flex items-center justify-center gap-2 border-[1px] border-header-login-bg;
    }
  }

  .btn-login {
    @apply bg-header-login-bg text-header-login-text py-2 px-[.67rem] rounded text-base font-semibold xl:px-[.8rem] whitespace-nowrap flex items-center justify-center gap-2;

    &.new {
      @apply bg-transparent text-header-login-bg border-[1px] border-header-login-bg text-[12px] md:text-[14px] py-1.5 md:py-2 px-3 md:px-4 leading-3;
      svg {
        @apply hidden md:block;
      }
    }
  }
}
</style>

<style module lang="postcss">
.registerPill {
  @apply absolute top-[-10px] md:top-[-8px] right-0 flex justify-center items-center rounded-3xl gap-[2px] text-[8px] h-[14px] w-[40px] font-bold bg-[#0062FF] text-white;

  img {
    @apply w-3;
  }
}
</style>
